// src/components/Pinjaman.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  getLoans,
  getLoanById,
  createLoan,
  updateLoan,
  deleteLoan,
  submitLoan,
  approveLoan,
  rejectLoan,
  calculateLoan,
  payInstallment,
  formatCurrency,
  formatDate,
  getStatusColor,
  getInstallmentStatusColor,
} from "../api/loans";

/**
 * Member Search Input Component
 */
const MemberSearchInput = ({ api, value, onChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const isSelectedRef = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (isSelectedRef.current) {
      isSelectedRef.current = false;
      return;
    }
    if (query.length < 2) { setResults([]); setOpen(false); return; }
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/members?search=${encodeURIComponent(query)}&per_page=10`);
        const list = data.data || data;
        setResults(list);
        setOpen(true);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, api]);

  const handleSelect = (member) => {
    isSelectedRef.current = true;
    setSelected(member);
    setQuery(member.name);
    setResults([]);
    setOpen(false);
    onChange(member.id);
  };

  const handleClear = () => {
    isSelectedRef.current = false;
    setSelected(null);
    setQuery("");
    setResults([]);
    setOpen(false);
    onChange("");
  };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <input
          className="w-full rounded-xl border px-3 py-2 pr-8"
          placeholder="Ketik nama atau no. anggota..."
          value={query}
          onChange={(e) => {
            isSelectedRef.current = false;
            setSelected(null);
            setQuery(e.target.value);
            onChange("");
          }}
          autoComplete="off"
        />
        {(query || selected) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 text-sm"
          >
            ×
          </button>
        )}
      </div>
      {loading && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border bg-white shadow-lg px-4 py-2 text-xs text-slate-400">
          Mencari...
        </div>
      )}
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border bg-white shadow-lg py-1 max-h-48 overflow-y-auto">
          {results.map((m) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(m)}
              className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 text-slate-700"
            >
              <span className="font-medium">{m.name}</span>
              <span className="ml-2 text-slate-400">{m.member_no}</span>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && !loading && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border bg-white shadow-lg px-4 py-2 text-xs text-slate-400">
          Anggota tidak ditemukan
        </div>
      )}
      {selected && (
        <p className="mt-1 text-xs text-slate-400">
          ID: {selected.id} · {selected.member_no}
        </p>
      )}
    </div>
  );
};

/**
 * Loan Calculator Component
 */
const LoanCalculator = ({ onCalculate, defaultRate }) => {
  const [form, setForm] = useState({
    loan_amount: "",
    tenor_months: "",
    interest_rate: defaultRate || "2.0",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (defaultRate) {
      setForm((f) => ({ ...f, interest_rate: defaultRate }));
    }
  }, [defaultRate]);

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await onCalculate({
        loan_amount: parseFloat(form.loan_amount),
        tenor_months: parseInt(form.tenor_months),
        interest_rate: parseFloat(form.interest_rate),
      });
      setResult(data);
    } catch (error) {
      alert("Gagal menghitung: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border p-5">
      <h3 className="text-lg font-semibold mb-4">Kalkulator Pinjaman</h3>
      <form onSubmit={handleCalculate} className="grid md:grid-cols-4 gap-3 mb-4">
        <input
          className="rounded-xl border px-3 py-2"
          placeholder="Jumlah Pinjaman (min 100.000)"
          type="number"
          min="100000"
          step="1000"
          value={form.loan_amount}
          onChange={(e) => setForm({ ...form, loan_amount: e.target.value })}
          required
        />
        <input
          className="rounded-xl border px-3 py-2"
          placeholder="Tenor (bulan)"
          type="number"
          min="1"
          max="60"
          value={form.tenor_months}
          onChange={(e) => setForm({ ...form, tenor_months: e.target.value })}
          required
        />
        <input
          className="rounded-xl border px-3 py-2"
          placeholder="Bunga per bulan (%)"
          type="number"
          step="0.1"
          min="0"
          value={form.interest_rate}
          onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
          required
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2 disabled:opacity-50"
        >
          {loading ? "Menghitung..." : "Hitung"}
        </button>
      </form>

      {result && (
        <div className="grid md:grid-cols-3 gap-3">
          <div className="rounded-xl bg-blue-50 p-4">
            <div className="text-xs text-blue-600 mb-1">Angsuran per Bulan</div>
            <div className="text-xl font-bold text-blue-700">
              {formatCurrency(result.calculation.monthly_installment)}
            </div>
          </div>
          <div className="rounded-xl bg-green-50 p-4">
            <div className="text-xs text-green-600 mb-1">Total Pembayaran</div>
            <div className="text-xl font-bold text-green-700">
              {formatCurrency(result.calculation.total_payment)}
            </div>
          </div>
          <div className="rounded-xl bg-purple-50 p-4">
            <div className="text-xs text-purple-600 mb-1">Total Bunga</div>
            <div className="text-xl font-bold text-purple-700">
              {formatCurrency(result.calculation.total_interest)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Loan Form Component (Create/Edit)
 */
const LoanForm = ({ api, loan, onSuccess, onCancel, userRole, defaultRate }) => {
  const [form, setForm] = useState({
    member_id: loan?.member_id || "",
    loan_type: loan?.loan_type || "usaha",
    loan_amount: loan?.loan_amount || "",
    tenor_months: loan?.tenor_months || "",
    interest_rate: loan?.interest_rate || defaultRate || "2.0",
    purpose: loan?.purpose || "",
    status: loan?.status || "draft",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (defaultRate && !loan) {
      setForm((f) => ({ ...f, interest_rate: defaultRate }));
    }
  }, [defaultRate, loan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        member_id: parseInt(form.member_id),
        loan_amount: parseFloat(form.loan_amount),
        tenor_months: parseInt(form.tenor_months),
        interest_rate: parseFloat(form.interest_rate),
      };
      if (loan) {
        await updateLoan(api, loan.id, payload);
        alert("Pinjaman berhasil diperbarui");
      } else {
        await createLoan(api, payload);
        alert("Pengajuan pinjaman berhasil dibuat");
      }
      onSuccess();
    } catch (error) {
      alert("Gagal menyimpan: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border p-5">
      <h3 className="text-lg font-semibold mb-4">
        {loan ? "Edit Pinjaman" : "Pengajuan Pinjaman Baru"}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          {(userRole === "administrator" || userRole === "pegawai") && (
            <div>
              <label className="block text-sm font-medium mb-1">Anggota *</label>
              <MemberSearchInput
                api={api}
                value={form.member_id}
                onChange={(id) => setForm((f) => ({ ...f, member_id: id }))}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Jenis Pinjaman *</label>
            <select
              className="w-full rounded-xl border px-3 py-2"
              value={form.loan_type}
              onChange={(e) => setForm({ ...form, loan_type: e.target.value })}
              required
            >
              <option value="usaha">Pinjaman Usaha</option>
              <option value="konsumtif">Pinjaman Konsumtif</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Jumlah Pinjaman *</label>
            <input
              className="w-full rounded-xl border px-3 py-2"
              type="number"
              min="100000"
              step="1000"
              value={form.loan_amount}
              onChange={(e) => setForm({ ...form, loan_amount: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tenor (Bulan) *</label>
            <input
              className="w-full rounded-xl border px-3 py-2"
              type="number"
              min="1"
              max="60"
              value={form.tenor_months}
              onChange={(e) => setForm({ ...form, tenor_months: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Bunga per Bulan (%) *
              {defaultRate && (
                <span className="ml-2 text-xs text-slate-400 font-normal">
                  default: {defaultRate}%
                </span>
              )}
            </label>
            <input
              className="w-full rounded-xl border px-3 py-2"
              type="number"
              step="0.1"
              min="0"
              value={form.interest_rate}
              onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              className="w-full rounded-xl border px-3 py-2"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="draft">Draft</option>
              <option value="pending">Pending (Langsung Ajukan)</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tujuan Pinjaman *</label>
          <textarea
            className="w-full rounded-xl border px-3 py-2"
            rows="3"
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
            required
          />
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4 py-2 disabled:opacity-50"
          >
            {loading ? "Menyimpan..." : loan ? "Update" : "Simpan"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl px-4 py-2"
          >
            Batal
          </button>
        </div>
      </form>
    </div>
  );
};

/**
 * Proof Viewer Modal — untuk admin lihat bukti transfer
 */
const ProofViewerModal = ({ api, inst, loanId, onClose, onVerified }) => {
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [showReject, setShowReject] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8082";
  const proofUrl = inst.payment_proof
    ? `${API_BASE}/storage/${inst.payment_proof}`
    : null;

  const isPDF = inst.payment_proof?.toLowerCase().endsWith(".pdf");

  const handleVerify = async (action) => {
    if (action === "reject" && !notes.trim()) {
      alert("Catatan penolakan harus diisi.");
      return;
    }
    try {
      setVerifyLoading(true);
      await api.post(
        `/loans/${loanId}/installments/${inst.id}/verify-payment`,
        { action, verification_notes: notes }
      );
      alert(action === "verify" ? "Pembayaran berhasil diverifikasi!" : "Bukti pembayaran ditolak.");
      onVerified();
      onClose();
    } catch (error) {
      alert("Gagal: " + (error.response?.data?.message || error.message));
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold">Bukti Pembayaran Angsuran #{inst.installment_number}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-slate-50 rounded-xl text-sm">
          <div>
            <div className="text-slate-500">Jumlah Bayar</div>
            <div className="font-semibold">{formatCurrency(inst.paid_amount)}</div>
          </div>
          <div>
            <div className="text-slate-500">Referensi</div>
            <div className="font-semibold">{inst.payment_reference || "-"}</div>
          </div>
          <div>
            <div className="text-slate-500">Catatan Anggota</div>
            <div className="font-semibold">{inst.notes || "-"}</div>
          </div>
          <div>
            <div className="text-slate-500">Dikirim Pada</div>
            <div className="font-semibold">
              {inst.submitted_at ? new Date(inst.submitted_at).toLocaleString("id-ID") : "-"}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="text-sm font-medium text-slate-600 mb-2">File Bukti Transfer:</div>
          {proofUrl ? (
            isPDF ? (
              <div className="border rounded-xl p-4 text-center">
                <p className="text-slate-500 text-sm mb-2">File PDF</p>
                <a
                  href={proofUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4 py-2 text-sm inline-block"
                >
                  Buka PDF
                </a>
              </div>
            ) : (
              <div className="border rounded-xl overflow-hidden">
                <img
                  src={proofUrl}
                  alt="Bukti Transfer"
                  className="w-full object-contain max-h-96"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "block";
                  }}
                />
                <div style={{ display: "none" }} className="p-4 text-center text-slate-400 text-sm">
                  Gagal memuat gambar.{" "}
                  <a href={proofUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                    Buka langsung
                  </a>
                </div>
              </div>
            )
          ) : (
            <div className="border rounded-xl p-4 text-center text-slate-400 text-sm">
              File bukti tidak ditemukan.
            </div>
          )}
        </div>

        <div className="space-y-3">
          {!showReject ? (
            <div className="flex gap-2">
              <button
                onClick={() => handleVerify("verify")}
                disabled={verifyLoading}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-xl py-2 text-sm font-medium"
              >
                {verifyLoading ? "Memproses..." : "Verifikasi Pembayaran"}
              </button>
              <button
                onClick={() => setShowReject(true)}
                disabled={verifyLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl py-2 text-sm font-medium"
              >
                Tolak Bukti
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                className="w-full rounded-xl border px-3 py-2 text-sm"
                placeholder="Alasan penolakan..."
                rows="2"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleVerify("reject")}
                  disabled={verifyLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl py-2 text-sm font-medium"
                >
                  {verifyLoading ? "Memproses..." : "Konfirmasi Penolakan"}
                </button>
                <button
                  onClick={() => { setShowReject(false); setNotes(""); }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl py-2 text-sm"
                >
                  Batal
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Loan Detail Modal
 */
const LoanDetailModal = ({ api, loanId, onClose, userRole, onRefresh }) => {
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentForm, setPaymentForm] = useState({
    installmentId: null,
    amount: "",
    payment_reference: "",
    notes: "",
  });
  const [uploadForm, setUploadForm] = useState({
    installmentId: null,
    amount: "",
    payment_reference: "",
    notes: "",
    file: null,
  });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [viewingProofInst, setViewingProofInst] = useState(null);

  const fetchLoanDetail = async () => {
    try {
      setLoading(true);
      const data = await getLoanById(api, loanId);
      setLoan(data);
    } catch (error) {
      alert("Gagal memuat detail: " + (error.response?.data?.message || error.message));
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoanDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanId]);

  const handleApprove = async () => {
    if (!window.confirm("Setujui pinjaman ini?")) return;
    try {
      await approveLoan(api, loanId);
      alert("Pinjaman berhasil disetujui");
      onRefresh();
      onClose();
    } catch (error) {
      alert("Gagal menyetujui: " + (error.response?.data?.message || error.message));
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert("Alasan penolakan harus diisi");
      return;
    }
    if (!window.confirm("Tolak pinjaman ini?")) return;
    try {
      await rejectLoan(api, loanId, rejectionReason);
      alert("Pinjaman ditolak");
      onRefresh();
      onClose();
    } catch (error) {
      alert("Gagal menolak: " + (error.response?.data?.message || error.message));
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      await payInstallment(api, loanId, paymentForm.installmentId, {
        amount: parseFloat(paymentForm.amount),
        payment_reference: paymentForm.payment_reference,
        notes: paymentForm.notes,
      });
      alert("Pembayaran berhasil dicatat");
      setPaymentForm({ installmentId: null, amount: "", payment_reference: "", notes: "" });
      fetchLoanDetail();
      onRefresh();
    } catch (error) {
      alert("Gagal mencatat pembayaran: " + (error.response?.data?.message || error.message));
    }
  };

  const handleUploadPayment = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      alert("Bukti pembayaran harus diupload.");
      return;
    }
    try {
      setUploadLoading(true);
      const formData = new FormData();
      formData.append("amount", uploadForm.amount);
      formData.append("payment_reference", uploadForm.payment_reference);
      formData.append("notes", uploadForm.notes || "");
      formData.append("payment_proof", uploadForm.file);

      await api.post(
        `/loans/${loanId}/installments/${uploadForm.installmentId}/submit-payment`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      alert("Bukti pembayaran berhasil dikirim, menunggu verifikasi admin.");
      setUploadForm({ installmentId: null, amount: "", payment_reference: "", notes: "", file: null });
      fetchLoanDetail();
      onRefresh();
    } catch (error) {
      alert("Gagal upload: " + (error.response?.data?.message || error.message));
    } finally {
      setUploadLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6">
          <p>Memuat detail...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-semibold">Detail Pinjaman</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <div className="text-sm text-gray-600">No. Kontrak</div>
              <div className="font-semibold">{loan.contract_no || "-"}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Status</div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(loan.status)}`}>
                {loan.status_label}
              </span>
            </div>
            <div>
              <div className="text-sm text-gray-600">Nama Anggota</div>
              <div className="font-semibold">{loan.member?.name || "-"}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Jenis Pinjaman</div>
              <div className="font-semibold">{loan.loan_type_label}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Jumlah Pinjaman</div>
              <div className="font-semibold text-blue-600">{formatCurrency(loan.loan_amount)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Tenor</div>
              <div className="font-semibold">{loan.tenor_months} Bulan</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Bunga</div>
              <div className="font-semibold">{loan.interest_rate}% per bulan</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Angsuran per Bulan</div>
              <div className="font-semibold text-green-600">{formatCurrency(loan.monthly_installment)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Pembayaran</div>
              <div className="font-semibold">{formatCurrency(loan.total_payment)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Sisa Pokok</div>
              <div className="font-semibold text-orange-600">{formatCurrency(loan.remaining_principal)}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-sm text-gray-600">Tujuan</div>
              <div className="font-semibold">{loan.purpose}</div>
            </div>
          </div>

          {loan.status === "pending" && (userRole === "administrator" || userRole === "pegawai") && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-xl">
              <h4 className="font-semibold mb-3">Tindakan Persetujuan</h4>
              <div className="flex gap-2 mb-3">
                <button
                  onClick={handleApprove}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-4 py-2"
                >
                  Setujui
                </button>
                <button
                  onClick={() => setRejectionReason(rejectionReason ? "" : " ")}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2"
                >
                  Tolak
                </button>
              </div>
              {rejectionReason !== "" && (
                <div>
                  <textarea
                    className="w-full rounded-xl border px-3 py-2 mb-2"
                    placeholder="Alasan penolakan..."
                    rows="2"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  />
                  <button
                    onClick={handleReject}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2"
                  >
                    Konfirmasi Penolakan
                  </button>
                </div>
              )}
            </div>
          )}

          {loan.installments && loan.installments.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3">Jadwal Angsuran</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2">No</th>
                      <th className="text-left px-3 py-2">Jatuh Tempo</th>
                      <th className="text-right px-3 py-2">Pokok</th>
                      <th className="text-right px-3 py-2">Bunga</th>
                      <th className="text-right px-3 py-2">Total</th>
                      <th className="text-right px-3 py-2">Dibayar</th>
                      <th className="text-center px-3 py-2">Status</th>
                      <th className="text-center px-3 py-2">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loan.installments.map((inst) => (
                      <tr key={inst.id} className="border-t">
                        <td className="px-3 py-2">{inst.installment_number}</td>
                        <td className="px-3 py-2">{formatDate(inst.due_date)}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(inst.principal_amount)}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(inst.interest_amount)}</td>
                        <td className="px-3 py-2 text-right font-semibold">{formatCurrency(inst.total_amount)}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(inst.paid_amount)}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${getInstallmentStatusColor(inst.status)}`}>
                            {inst.status_label}
                          </span>
                        </td>

                        {(userRole === "administrator" || userRole === "pegawai") && (
                          <td className="px-3 py-2 text-center">
                            <div className="flex gap-1 justify-center">
                              {inst.verification_status === "pending" && inst.payment_proof && (
                                <button
                                  onClick={() => setViewingProofInst(inst)}
                                  className="px-2 py-1 rounded-lg bg-yellow-100 text-yellow-700 hover:bg-yellow-200 text-xs font-medium"
                                >
                                  Lihat Bukti
                                </button>
                              )}
                              {inst.status !== "paid" && inst.verification_status !== "pending" && (
                                <button
                                  onClick={() => setPaymentForm({
                                    installmentId: inst.id,
                                    amount: (inst.total_amount - inst.paid_amount).toString(),
                                    payment_reference: "",
                                    notes: "",
                                  })}
                                  className="text-blue-600 hover:text-blue-700 text-xs"
                                >
                                  Bayar
                                </button>
                              )}
                            </div>
                          </td>
                        )}

                        {userRole === "anggota" && (
                          <td className="px-3 py-2 text-center">
                            {inst.status === "paid" ? (
                              <span className="text-xs text-green-600">Lunas</span>
                            ) : inst.verification_status === "pending" ? (
                              <span className="text-xs text-yellow-600">Menunggu verifikasi</span>
                            ) : inst.verification_status === "rejected" ? (
                              <button
                                onClick={() => setUploadForm({
                                  installmentId: inst.id,
                                  amount: (inst.total_amount - inst.paid_amount).toString(),
                                  payment_reference: "",
                                  notes: "",
                                  file: null,
                                })}
                                className="text-red-600 hover:text-red-700 text-xs"
                              >
                                Upload Ulang
                              </button>
                            ) : (
                              <button
                                onClick={() => setUploadForm({
                                  installmentId: inst.id,
                                  amount: (inst.total_amount - inst.paid_amount).toString(),
                                  payment_reference: "",
                                  notes: "",
                                  file: null,
                                })}
                                className="text-purple-600 hover:text-purple-700 text-xs"
                              >
                                Upload Bukti
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {paymentForm.installmentId && (
            <div className="mt-4 p-4 bg-blue-50 rounded-xl">
              <h4 className="font-semibold mb-3">Catat Pembayaran</h4>
              <form onSubmit={handlePayment} className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <input
                    className="rounded-xl border px-3 py-2"
                    placeholder="Jumlah Pembayaran"
                    type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    required
                  />
                  <input
                    className="rounded-xl border px-3 py-2"
                    placeholder="Referensi Pembayaran (opsional)"
                    value={paymentForm.payment_reference}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })}
                  />
                </div>
                <input
                  className="w-full rounded-xl border px-3 py-2"
                  placeholder="Catatan (opsional)"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4 py-2"
                  >
                    Simpan Pembayaran
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentForm({ installmentId: null, amount: "", payment_reference: "", notes: "" })}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl px-4 py-2"
                  >
                    Batal
                  </button>
                </div>
              </form>
            </div>
          )}

          {uploadForm.installmentId && (
            <div className="mt-4 p-4 bg-purple-50 rounded-xl">
              <h4 className="font-semibold mb-3">Upload Bukti Pembayaran</h4>
              <form onSubmit={handleUploadPayment} className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <input
                    className="rounded-xl border px-3 py-2"
                    placeholder="Jumlah Pembayaran"
                    type="number"
                    step="0.01"
                    value={uploadForm.amount}
                    onChange={(e) => setUploadForm({ ...uploadForm, amount: e.target.value })}
                    required
                  />
                  <input
                    className="rounded-xl border px-3 py-2"
                    placeholder="Referensi Transfer (no. transaksi)"
                    value={uploadForm.payment_reference}
                    onChange={(e) => setUploadForm({ ...uploadForm, payment_reference: e.target.value })}
                    required
                  />
                </div>
                <input
                  className="w-full rounded-xl border px-3 py-2"
                  placeholder="Catatan (opsional)"
                  value={uploadForm.notes}
                  onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                />
                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Bukti Transfer (jpg/png/pdf, maks 5MB)
                  </label>
                  <input
                    className="w-full rounded-xl border px-3 py-2 text-sm"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={uploadLoading}
                    className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-xl px-4 py-2"
                  >
                    {uploadLoading ? "Mengupload..." : "Kirim Bukti Bayar"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadForm({ installmentId: null, amount: "", payment_reference: "", notes: "", file: null })}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl px-4 py-2"
                  >
                    Batal
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {viewingProofInst && (
        <ProofViewerModal
          api={api}
          inst={viewingProofInst}
          loanId={loanId}
          onClose={() => setViewingProofInst(null)}
          onVerified={() => {
            fetchLoanDetail();
            onRefresh();
          }}
        />
      )}
    </>
  );
};

/**
 * Main Pinjaman Component
 */
export default function PinjamanPage({ api, auth, initialLoanId, onClearInitialLoan }) {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [defaultRate, setDefaultRate] = useState("2.0");
  const [filters, setFilters] = useState({
    status: "",
    loan_type: "",
    search: "",
  });

  const userRole = auth?.user?.role || "anggota";
  const isAdmin = userRole === "administrator" || userRole === "pegawai";
  const userId = auth?.user?.id;

  useEffect(() => {
    fetchDefaultRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLoans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // ✅ Auto-buka modal jika ada initialLoanId dari Dashboard
  useEffect(() => {
    if (initialLoanId) {
      setSelectedLoanId(initialLoanId);
      // Reset initialLoanId di App.js supaya tidak re-trigger kalau balik ke halaman ini
      if (onClearInitialLoan) onClearInitialLoan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoanId]);

  const fetchDefaultRate = async () => {
    try {
      const { data } = await api.get("/settings");
      if (data?.bunga_pinjaman?.value) {
        setDefaultRate(data.bunga_pinjaman.value);
      }
    } catch (e) {
      console.error("Gagal fetch settings:", e);
    }
  };

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const filterParams = isAdmin ? filters : { ...filters, member_id: userId };
      const data = await getLoans(api, filterParams);
      setLoans(data.data || data);
    } catch (error) {
      console.error(error);
      alert("Gagal memuat data pinjaman");
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async (params) => {
    return await calculateLoan(api, params);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus pinjaman ini?")) return;
    try {
      await deleteLoan(api, id);
      alert("Pinjaman berhasil dihapus");
      fetchLoans();
    } catch (error) {
      alert("Gagal menghapus: " + (error.response?.data?.message || error.message));
    }
  };

  const handleSubmit = async (id) => {
    if (!window.confirm("Ajukan pinjaman ini untuk persetujuan?")) return;
    try {
      await submitLoan(api, id);
      alert("Pinjaman berhasil diajukan");
      fetchLoans();
    } catch (error) {
      alert("Gagal mengajukan: " + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Manajemen Pinjaman</h2>
        <button
          onClick={() => { setShowForm(true); setEditingLoan(null); }}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4 py-2"
        >
          + Pengajuan Baru
        </button>
      </div>

      <LoanCalculator onCalculate={handleCalculate} defaultRate={defaultRate} />

      {showForm && (
        <LoanForm
          api={api}
          loan={editingLoan}
          userRole={userRole}
          defaultRate={defaultRate}
          onSuccess={() => { setShowForm(false); setEditingLoan(null); fetchLoans(); }}
          onCancel={() => { setShowForm(false); setEditingLoan(null); }}
        />
      )}

      <div className="rounded-2xl bg-white border p-4">
        <div className="grid md:grid-cols-4 gap-3">
          <input
            className="rounded-xl border px-3 py-2"
            placeholder="Cari no. kontrak..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <select
            className="rounded-xl border px-3 py-2"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">Semua Status</option>
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
            <option value="active">Aktif</option>
            <option value="completed">Lunas</option>
            <option value="rejected">Ditolak</option>
          </select>
          <select
            className="rounded-xl border px-3 py-2"
            value={filters.loan_type}
            onChange={(e) => setFilters({ ...filters, loan_type: e.target.value })}
          >
            <option value="">Semua Jenis</option>
            <option value="usaha">Pinjaman Usaha</option>
            <option value="konsumtif">Pinjaman Konsumtif</option>
          </select>
          <button
            onClick={() => setFilters({ status: "", loan_type: "", search: "" })}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl px-4 py-2"
          >
            Reset Filter
          </button>
        </div>
      </div>

      <div className="rounded-2xl bg-white border overflow-hidden">
        {loading ? (
          <div className="p-5 text-center text-gray-500">Memuat data...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3">No. Kontrak</th>
                  <th className="text-left px-4 py-3">Anggota</th>
                  <th className="text-left px-4 py-3">Jenis</th>
                  <th className="text-right px-4 py-3">Jumlah</th>
                  <th className="text-center px-4 py-3">Tenor</th>
                  <th className="text-right px-4 py-3">Angsuran/Bln</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-center px-4 py-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loans.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-4 py-3 text-center text-gray-400">
                      Belum ada data pinjaman
                    </td>
                  </tr>
                )}
                {loans.map((loan) => (
                  <tr key={loan.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{loan.contract_no || "-"}</td>
                    <td className="px-4 py-3">{loan.member?.name || "-"}</td>
                    <td className="px-4 py-3">{loan.loan_type_label}</td>
                    <td className="px-4 py-3 text-right font-semibold text-blue-600">
                      {formatCurrency(loan.loan_amount)}
                    </td>
                    <td className="px-4 py-3 text-center">{loan.tenor_months} bln</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(loan.monthly_installment)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(loan.status)}`}>
                        {loan.status_label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => setSelectedLoanId(loan.id)}
                          className="text-blue-600 hover:text-blue-700 text-xs"
                        >
                          Detail
                        </button>
                        {isAdmin && loan.status === "draft" && (
                          <>
                            <button
                              onClick={() => { setEditingLoan(loan); setShowForm(true); }}
                              className="text-green-600 hover:text-green-700 text-xs"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleSubmit(loan.id)}
                              className="text-purple-600 hover:text-purple-700 text-xs"
                            >
                              Ajukan
                            </button>
                            <button
                              onClick={() => handleDelete(loan.id)}
                              className="text-red-600 hover:text-red-700 text-xs"
                            >
                              Hapus
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {loans.length > 0 && (
        <div className="grid md:grid-cols-4 gap-4">
          <div className="rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white p-5">
            <div className="text-sm opacity-90 mb-1">Total Pinjaman Aktif</div>
            <div className="text-2xl font-bold">
              {loans.filter((l) => l.status === "active").length}
            </div>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-green-500 to-green-600 text-white p-5">
            <div className="text-sm opacity-90 mb-1">Total Nilai Pinjaman</div>
            <div className="text-xl font-bold">
              {formatCurrency(
                loans.filter((l) => l.status === "active")
                  .reduce((sum, l) => sum + parseFloat(l.loan_amount || 0), 0)
              )}
            </div>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-5">
            <div className="text-sm opacity-90 mb-1">Menunggu Persetujuan</div>
            <div className="text-2xl font-bold">
              {loans.filter((l) => l.status === "pending").length}
            </div>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 text-white p-5">
            <div className="text-sm opacity-90 mb-1">Pinjaman Lunas</div>
            <div className="text-2xl font-bold">
              {loans.filter((l) => l.status === "completed").length}
            </div>
          </div>
        </div>
      )}

      {selectedLoanId && (
        <LoanDetailModal
          api={api}
          loanId={selectedLoanId}
          userRole={userRole}
          onClose={() => setSelectedLoanId(null)}
          onRefresh={fetchLoans}
        />
      )}
    </div>
  );
}